import React, { useCallback, useRef } from 'react';
import type { DrawerProps } from '@mantine/core';
import { DrawerController as DrawerControllerClass } from '@ayocore/platform/client/components/DrawerHost/DrawerController';

type DrawerConfig = Omit<DrawerProps, 'opened' | 'onClose' | 'children' | 'title'> & {
  loading?: boolean;
};

export interface DrawerOptions<P extends Record<string, unknown>> {
  /** Если передана — в drawer появляется кнопка "В новой вкладке". Получает props текущего drawer. */
  openInTab?: (props: P) => void;
}

export interface DrawerController<P extends Record<string, unknown>> {
  open: (props: P & { title?: string }, config?: DrawerConfig) => string;
  close: () => void;
  onClose: (callback: () => void) => void;
}

export function useDrawer<P extends Record<string, unknown>>(
  Component: React.ComponentType<P>,
  options?: DrawerOptions<P>
): DrawerController<P> {
  const drawerIdRef = useRef<string | null>(null);
  const onCloseCallbackRef = useRef<(() => void) | null>(null);
  const controller = DrawerControllerClass.getInstance();

  const handleDrawerClosed = useCallback(() => {
    drawerIdRef.current = null;
    onCloseCallbackRef.current?.();
  }, []);

  const open = useCallback(
    (props: P & { title?: string }, config?: DrawerConfig): string => {
      // title извлекаем из props, но не передаём в Drawer — он не нужен
      const { title: _title, ...componentProps } = props;
      const { loading = false, ...drawerProps } = config ?? {};

      const id =
        drawerIdRef.current || `drawer-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
      drawerIdRef.current = id;

      const onOpenInTab = options?.openInTab
        ? () => {
            controller.close(id);
            options.openInTab!(props as P);
          }
        : undefined;

      controller.open(
        id,
        <Component {...(componentProps as P)} />,
        {
          ...drawerProps,
        },
        [handleDrawerClosed],
        loading,
        onOpenInTab
      );

      return id;
    },
    [Component, controller, handleDrawerClosed]
  );

  const close = useCallback(() => {
    const drawerId = drawerIdRef.current;
    if (drawerId) {
      controller.close(drawerId);
      drawerIdRef.current = null;
    }
  }, [controller]);

  const onClose = useCallback((callback: () => void) => {
    onCloseCallbackRef.current = callback;
  }, []);

  return {
    open,
    close,
    onClose,
  };
}
